import type { TFunction } from "i18next";
import { FileSpreadsheet } from "lucide-react";
import { lazy, Suspense, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Alert } from "@krea/shared/design-system/alerts/Alert";
import { Badge } from "@krea/shared/design-system/Badge";
import { Button } from "@krea/shared/design-system/Button";
import { Input } from "@krea/shared/design-system/input/input";
import { LabeledSlider } from "@krea/shared/design-system/LabeledSlider";
import { Switch } from "@krea/shared/design-system/Switch";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@krea/shared/design-system/Tabs";
import { Text } from "@krea/shared/design-system/Text";
import {
  describeEarlyRepayment,
  EarlyRepaymentCommentBox,
  EarlyRepaymentType,
  EarlyRepaymentUnit,
} from "@krea/shared/features/early-repayment";
import { PaymentFrequency } from "@krea/shared/types/common";

import { buildRepaymentPlanExportSpec } from "./buildRepaymentPlanExportSpec";
import type { IEarlyRepaymentPlan } from "./earlyRepayment";
import { isSetupFeeRow, PlanInfoNote } from "./planRows";
import type { IRepaymentChartRow } from "./RepaymentPlanChart";
import type {
  IExtendedTermState,
  IRepaymentPlanBidInfo,
  IRepaymentPlanData,
  IRepaymentPlanEntryData,
} from "./types";
import {
  type IRepaymentPlanDisplay,
  MONEY_COLUMNS,
  useRepaymentPlanDisplay,
} from "./useRepaymentPlanDisplay";

// Long enough that a multi-digit term is typed before anything fires, short
// enough that the admin never has to click away to see the result. Each
// commit is a network round trip whose resolver runs the rate solve.
const MONTHS_COMMIT_DEBOUNCE_MS = 500;

const RepaymentPlanChart = lazy(() =>
  import("./RepaymentPlanChart").then((m) => ({
    default: m.RepaymentPlanChart,
  })),
);

const TYPE_ONLY_KEY: Record<EarlyRepaymentType, string> = {
  [EarlyRepaymentType.FREE]: "bids.common.earlyRepaymentFree",
  [EarlyRepaymentType.FREE_AFTER_PERIOD]:
    "bids.common.earlyRepaymentFreeAfterPeriod",
  [EarlyRepaymentType.FEE_APPLIES]: "bids.common.earlyRepaymentFeeApplies",
  [EarlyRepaymentType.FULL_INTEREST_AND_FEES]:
    "bids.common.earlyRepaymentAllCostsApply",
};

interface IRepaymentPlanSectionProps {
  bid: IRepaymentPlanBidInfo;
  // The caller decides whether to render this section at all (only when a
  // plan exists), so by the time we render a plan is always present.
  plan: IRepaymentPlanData;
  formatCurrency: (value: string | number) => string;
  formatPaybackTime: (months: number) => string;
  companyName: string | null;
  // Used to name the exported file: amortizationplan_<loanApp>_offer_<lender>_<bid>.
  loanApplicationId: string | null;
  lenderName: string | null;
  /** Gates the internal tools: the shorter-term slider, "Simulate a longer
   * term" and the Excel export. Required rather than defaulted, so a new
   * caller has to decide what a customer sees. */
  isAdmin: boolean;
  /** The view that opens first. */
  initialView: PlanView;
  /** KREA-4474: when present, an admin can simulate a longer term than the
   * lender quoted. Absent means the section behaves exactly as before. */
  extendedTerm?: IExtendedTermState;
}

type PlanView = "graph" | "table";

const StatBox = ({
  label,
  value,
  tooltip,
}: {
  label: string;
  value: string;
  // Optional plain-language explanation; when present an info icon is shown
  // that reveals it (hover tooltip on desktop, bottom drawer on mobile).
  tooltip?: string;
}) => (
  <div className="flex min-w-[7rem] flex-1 flex-col gap-1 rounded-lg border border-gray-200 bg-white p-3">
    <div className="flex items-center gap-1">
      <Text className="text-label">{label}</Text>
      {tooltip && <PlanInfoNote label={label} text={tooltip} />}
    </div>
    <Text className="text-base-strong tabular-nums">{value}</Text>
  </div>
);

// `plan` is always the lender's quoted offer -- never
// `activePlan`, which becomes the extended plan itself while the switch is
// on, which would make every delta zero.
const ExtendedCostIncreaseSummary = ({
  plan,
  extendedPlan,
  quotedMonths,
  isBullet,
  formatCurrency,
  t,
}: {
  plan: IRepaymentPlanData;
  extendedPlan: IRepaymentPlanData;
  quotedMonths: number;
  // BULLET/BUILDING_CREDIT: the monthly payment is interest-only and never
  // changes with term, so that box says nothing -- omit it and let the extra
  // interest stand alone.
  isBullet: boolean;
  formatCurrency: (value: string | number) => string;
  t: TFunction;
}) => {
  // extendedPlan's month-1 entry is guaranteed by the caller's `fullMonths >
  // 1` render guard (fullMonths follows activePlan, which IS extendedPlan
  // while the switch is on). plan's is expected too -- any real quoted offer
  // has at least one payment month -- but that isn't provable from
  // fullMonths, which says nothing about the quoted plan. Either way,
  // `noUncheckedIndexedAccess` can't see either invariant across the props
  // boundary, so narrow explicitly instead of a non-null assertion.
  const quotedMonthlyPayment = plan.entries[1]?.payment;
  const extendedMonthlyPayment = extendedPlan.entries[1]?.payment;
  const showMonthlyPayment =
    !isBullet &&
    quotedMonthlyPayment !== undefined &&
    extendedMonthlyPayment !== undefined;

  // The delta reads the two plans' TOTALS, so the arithmetic is the same
  // whatever the method. So is the tooltip: every method carries the extra
  // months at the rate the offer implies, so one string covers them all --
  // same single basis the banner states. There is no separate "extra total
  // cost" box: the plan carries no recurring fees, so the extra total repaid
  // is exactly the extra interest.
  const extraInterestBox = (
    <StatBox
      label={t("extended.costIncrease.extraInterest")}
      value={formatCurrency(
        Number(extendedPlan.totals.totalInterest) -
          Number(plan.totals.totalInterest),
      )}
      tooltip={t("extended.costIncrease.extraInterestTooltip")}
    />
  );

  return (
    <div className="mb-4" data-testid="extended-cost-increase">
      {/* One sentence, so a plain word space rather than a flex gap: the two
          halves read as "Extra costs compared to the N months quoted", and the
          gap-2 this used to sit in looked like a missing word. Inline flow also
          lets it wrap as a sentence instead of as two flex items. */}
      <Text className="mb-1 text-label">
        {t("extended.costIncrease.title")}{" "}
        <Text htmlTag="span" className="text-helper">
          {t("extended.costIncrease.versusQuoted", { months: quotedMonths })}
        </Text>
      </Text>
      <div className="flex flex-wrap gap-2">
        {showMonthlyPayment && (
          <StatBox
            label={t("extended.costIncrease.monthlyPayment")}
            value={`${formatCurrency(
              Number(extendedMonthlyPayment),
            )} (${formatCurrency(Number(quotedMonthlyPayment))})`}
            tooltip={t("extended.costIncrease.monthlyPaymentTooltip")}
          />
        )}
        {extraInterestBox}
      </div>
    </div>
  );
};

// The term controls: the input/read-only months swap, the switch, validation,
// the "simulation unavailable" message, and the banner+caveat+summary.
const RepaymentTermControls = ({
  fullMonths,
  months,
  quotedMonths,
  isExtended,
  isBullet,
  extendedTerm,
  monthsValue,
  monthsError,
  methodCaveatKey,
  plan,
  formatCurrency,
  lenderName,
  t,
  onMonthsChange,
  commitMonths,
  onSelectedMonthsChange,
  onSwitchToggle,
}: {
  fullMonths: number;
  months: number;
  quotedMonths: number;
  isExtended: boolean;
  isBullet: boolean;
  extendedTerm?: IExtendedTermState;
  monthsValue: string;
  monthsError: boolean;
  /** Null unless the method needs a caveat beyond `banner` -- only
   * BULLET/BUILDING_CREDIT does. */
  methodCaveatKey: string | null;
  plan: IRepaymentPlanData;
  formatCurrency: (value: string | number) => string;
  lenderName: string | null;
  t: TFunction;
  onMonthsChange: (value: string) => void;
  commitMonths: () => void;
  onSelectedMonthsChange: (value: number) => void;
  onSwitchToggle: (checked: boolean) => void;
}) => {
  const monthsList = Array.from({ length: fullMonths }, (_, i) => i + 1);
  // KREA-4474 duplicate-id fix: multiple RepaymentPlanSection instances can be
  // on screen at once (e.g. CompanyLoanOffers maps sortedLenderResponses into
  // one card per bid), and both AccordionItem and BidsListItemDesktop keep a
  // collapsed card's DOM mounted (they animate opacity/height rather than
  // unmounting) -- so every instance's controls exist in the DOM
  // simultaneously, not just the expanded one. A hardcoded id here would
  // collide across instances exactly like AccordionItem's own `resolvedId`
  // comment (packages/shared/design-system/AccordionItem.tsx) warns about;
  // useId() keeps each instance's ids unique the same way.
  const instanceId = useId();
  const switchId = `extended-switch-${instanceId}`;
  const monthsErrorId = `extended-months-error-${instanceId}`;
  // A committed longer term produced no plan -- either the backend's own R6
  // null-by-design (non-convergence, or a bid with no plan) or a fetch error
  // (the query now rethrows rather than swallowing). Gated on the COMMITTED
  // `extendedTerm.months` exceeding `minMonths`, not the `months` prop above:
  // while extended with no plan, the caller's `activePlan` falls back to the
  // quoted `plan`, so `months` reads as the quoted term regardless of what
  // was actually requested and could never distinguish "failed" from "not
  // asked yet".
  const showExtendedUnavailable =
    isExtended &&
    extendedTerm !== undefined &&
    !extendedTerm.isLoading &&
    !extendedTerm.plan &&
    extendedTerm.months > extendedTerm.minMonths;

  return (
    <div className="mb-4">
      <div className="mb-1 flex flex-wrap items-baseline gap-2">
        <Text className="text-label">{t("slider.label")}:</Text>

        {isExtended ? (
          <span className="flex items-baseline gap-1">
            <Input
              data-testid="extended-months-input"
              type="number"
              className="w-20"
              aria-label={t("extended.inputLabel")}
              aria-invalid={monthsError}
              aria-describedby={monthsError ? monthsErrorId : undefined}
              min={extendedTerm?.minMonths}
              max={extendedTerm?.maxMonths}
              value={monthsValue}
              onChange={(event) => onMonthsChange(event.target.value)}
              onBlur={commitMonths}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  commitMonths();
                }
              }}
            />
            <Text className="text-base-strong">
              {t("commons.month", { count: months })}
            </Text>
          </span>
        ) : (
          <Text className="text-base-strong">
            {months} {t("commons.month", { count: months })}
          </Text>
        )}

        <Text className="text-helper">
          ({t("slider.originalTerm")}: {quotedMonths}{" "}
          {t("commons.month", { count: quotedMonths })})
        </Text>

        {extendedTerm && (
          <span className="ml-auto flex items-center gap-2">
            {isExtended && extendedTerm.isLoading && (
              <Text className="text-helper">{t("extended.loading")}</Text>
            )}
            <Text htmlTag="label" htmlFor={switchId} className="text-label">
              {t("extended.switchLabel")}
            </Text>
            <Switch
              id={switchId}
              checked={extendedTerm.isOn}
              onCheckedChange={onSwitchToggle}
            />
          </span>
        )}
      </div>

      {isExtended && monthsError && (
        <Text
          id={monthsErrorId}
          role="alert"
          className="mb-2 block text-helper text-red"
        >
          {t("extended.validationRange", {
            min: extendedTerm?.minMonths,
            max: extendedTerm?.maxMonths,
          })}
        </Text>
      )}

      {showExtendedUnavailable && (
        <Text
          data-testid="extended-unavailable"
          className="mb-2 block text-helper"
        >
          {t("extended.unavailable")}
        </Text>
      )}

      {isExtended && extendedTerm?.plan && (
        <>
          <Alert
            variant="info"
            data-testid="extended-banner"
            // Alert hardcodes role="alert" (assertive), which would re-announce
            // this whole paragraph every time the simulated term changes, since
            // {{months}} is interpolated into it. This is persistent context,
            // not an interruption, so it goes back to the polite role.
            role="status"
          >
            <Alert.Description>
              <Text className="block text-helper">
                {t("extended.banner", {
                  months,
                  lenderName: lenderName ?? t("extended.lenderFallback"),
                })}
              </Text>
              {methodCaveatKey && (
                <div data-testid="extended-method-caveat">
                  <Text className="mt-1 block text-helper">
                    {t(methodCaveatKey)}
                  </Text>
                </div>
              )}
            </Alert.Description>
          </Alert>

          <ExtendedCostIncreaseSummary
            plan={plan}
            extendedPlan={extendedTerm.plan}
            quotedMonths={quotedMonths}
            isBullet={isBullet}
            formatCurrency={formatCurrency}
            t={t}
          />
        </>
      )}

      {!isExtended && (
        <div data-testid="repayment-months-slider">
          <LabeledSlider
            value={months}
            valuesList={monthsList}
            onChange={onSelectedMonthsChange}
            showMinMax={false}
          />
        </div>
      )}
    </div>
  );
};

// Only reachable through the admin's shorter-term slider: what settling
// early at that month would save, or, on an all-costs-apply offer, why it
// saves nothing.
const EarlyRepaymentPanels = ({
  displayPlan,
  hasNoCostReduction,
  comment,
  earlyRepaymentTermsLabel,
  formatCurrency,
  formatPaybackTime,
  t,
}: {
  displayPlan: IEarlyRepaymentPlan;
  hasNoCostReduction: boolean;
  comment: string | null;
  earlyRepaymentTermsLabel: string;
  formatCurrency: (value: string | number) => string;
  formatPaybackTime: (months: number) => string;
  t: TFunction;
}) => {
  if (!displayPlan.isEarly) {
    return null;
  }

  // Shared by both savings panels below. A fee of exactly 0 is a real
  // answer, so this tests `!== 0` rather than truthiness — a "0 kr" box would
  // assert a charge that does not exist.
  const settlementFeeStat =
    displayPlan.settlementFee !== null && displayPlan.settlementFee !== 0 ? (
      <StatBox
        label={t("earlySettlementFee")}
        value={formatCurrency(displayPlan.settlementFee)}
        tooltip={t("tooltips.earlySettlementFee")}
      />
    ) : null;

  return (
    <>
      {/* FULL_INTEREST_AND_FEES contracts that settling early saves nothing:
        its fee is `remaining_payment - balance`, exactly the interest that
        would otherwise fall due, so avoided and charged cancel and the net
        is always 0. A "Potential savings" panel there is true numbers spent
        telling the customer they save nothing — and a MINT one, which this
        design system reserves for success. One figure and one sentence say
        it instead (Adam's call).

        This branch is on the TYPE, and deliberately so: it picks which
        SECTION to render, which is the one genuinely type-specific
        question here. The arithmetic inside the savings panel stays gated
        on the fee, because a large FEE_APPLIES can also outweigh what is
        avoided and no type check would see it. */}
      {hasNoCostReduction && (
        <div
          className="-mx-4 -mb-4 mt-4 rounded-b-xl bg-gray-100 px-4 py-4 md:-mx-6 md:-mb-6 md:px-6"
          data-testid="repayment-no-cost-reduction"
        >
          <div className="flex flex-wrap gap-3">
            {settlementFeeStat}
            <StatBox
              label={t("savings.earlyRepaymentFee")}
              value={earlyRepaymentTermsLabel}
              tooltip={t("tooltips.earlyRepaymentFee")}
            />
          </div>
          {comment && (
            <EarlyRepaymentCommentBox
              label={t("bids.common.lendersComment")}
              comment={comment}
              className="mt-3 bg-white"
            />
          )}
        </div>
      )}

      {!hasNoCostReduction && (
        <div
          className="-mx-4 -mb-4 mt-4 rounded-b-xl bg-mint-100 px-4 py-4 md:-mx-6 md:-mb-6 md:px-6"
          data-testid="repayment-savings"
        >
          <Text className="text-base-strong mb-1 block">
            {t("savings.title")}
          </Text>
          <Text className="text-helper mb-3 block">
            {t("savings.repaidEarlier", {
              duration: formatPaybackTime(displayPlan.monthsSaved),
            })}
          </Text>
          <div className="flex flex-wrap gap-3">
            <StatBox
              label={t("savings.interestSaved")}
              value={formatCurrency(displayPlan.interestSaved)}
              tooltip={t("tooltips.interestSaved")}
            />
            {/* The deduction that turns the figure above into the net below.
              Gated on the FEE, not the bid type: a percentage fee on a
              large balance can outweigh what is avoided and drive the net
              negative, which no type check would catch. Same gate and same
              key as the XLSX export, which has always printed this row. */}
            {settlementFeeStat}
            {/* Net, only once the fee is known: without it the interest
              saved above is the whole story. */}
            {displayPlan.settlementFee !== null && (
              <StatBox
                label={t("savings.netSaved")}
                value={formatCurrency(displayPlan.netSaved)}
                tooltip={t("tooltips.netSaved")}
              />
            )}
            <StatBox
              label={t("savings.earlyRepaymentFee")}
              value={earlyRepaymentTermsLabel}
              tooltip={t("tooltips.earlyRepaymentFee")}
            />
          </div>
          {comment && (
            <EarlyRepaymentCommentBox
              label={t("bids.common.lendersComment")}
              comment={comment}
              className="mt-3 bg-white"
            />
          )}
        </div>
      )}
    </>
  );
};

// The full table: every money column, with the month-0 and early-repayment
// notes on the payment cell.
const RepaymentPlanTable = ({
  display,
  formatCurrency,
  t,
}: {
  display: IRepaymentPlanDisplay;
  formatCurrency: (value: string | number) => string;
  t: TFunction;
}) => {
  const { displayPlan, totalValue } = display;
  // Index access rather than .at(-1): one consuming app's TS lib target
  // predates ES2022, where Array.prototype.at does not exist.
  const finalEntry =
    displayPlan.entries[displayPlan.entries.length - 1] ?? null;

  const renderPaymentCell = (entry: IRepaymentPlanEntryData) => {
    // The early-repayment cost is already inside the truncated final row's
    // payment (deriveEarlyRepaymentPlan adds it); this only says so. Read off
    // the derived plan, which carries a fee only when a balloon row was built
    // to include it — a slider dragged back to the full term builds none. A
    // zero fee needs no explaining.
    const includedSettlementFee = displayPlan.settlementFee;
    const isSettlementCell =
      includedSettlementFee !== null &&
      includedSettlementFee !== 0 &&
      entry.month === finalEntry?.month;

    return (
      <td
        key="payment"
        className="border-t border-l p-1"
        data-testid={isSettlementCell ? "early-settlement-fee-cell" : undefined}
      >
        {/* One flex row: as a block span plus a w-full button the icon
            wrapped onto the next line. */}
        <div className="flex w-full items-center justify-end gap-1 px-1 py-1">
          <span className="text-table-cell tabular-nums">
            {formatCurrency(Number(entry.payment))}
          </span>
          {isSetupFeeRow(entry) && (
            <PlanInfoNote label={t("setupFee")} text={t("tooltips.setupFee")} />
          )}
          {isSettlementCell && (
            <PlanInfoNote
              label={t("earlySettlementFee")}
              text={t("tooltips.paymentIncludesSettlementFee", {
                fee: formatCurrency(includedSettlementFee),
              })}
            />
          )}
        </div>
      </td>
    );
  };

  return (
    // h-96 matches the chart height so switching views doesn't shift the
    // layout.
    <div className="h-96 overflow-auto rounded-lg border border-gray-200">
      <table className="w-full border-separate border-spacing-0 text-sm">
        <thead>
          <tr className="bg-gray-100">
            <th className="text-table-header sticky top-0 z-10 bg-gray-100 py-3 pl-3 pr-2 text-left">
              {t("month")}
            </th>
            {MONEY_COLUMNS.map((key) => (
              <th
                key={key}
                className="text-table-header sticky top-0 z-10 min-w-[120px] bg-gray-100 py-3 pr-2 text-right"
              >
                {t(key)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {displayPlan.entries.map((entry) => (
            <tr
              key={entry.month}
              data-testid="repayment-row"
              className="bg-white transition-colors hover:bg-primary-150"
            >
              <td className="border-t py-2 pl-3 pr-2 text-table-cell">
                {entry.month}
              </td>
              {MONEY_COLUMNS.map((key) =>
                key === "payment" ? (
                  renderPaymentCell(entry)
                ) : (
                  <td key={key} className="border-t border-l p-1">
                    <span className="block w-full px-1 py-1 text-right text-table-cell tabular-nums">
                      {formatCurrency(Number(entry[key]))}
                    </span>
                  </td>
                ),
              )}
            </tr>
          ))}

          <tr className="bg-gray-100 transition-colors hover:bg-gray-200">
            <td className="border-t py-2 pl-3 pr-2 text-table-cell font-semibold">
              {t("total")}
            </td>
            {MONEY_COLUMNS.map((key) => {
              const total = totalValue(key);
              return (
                <td key={key} className="border-l border-t p-1">
                  <span className="block w-full px-1 py-1 text-right text-table-cell font-semibold tabular-nums">
                    {total === null ? "" : formatCurrency(total)}
                  </span>
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export const RepaymentPlanSection = ({
  bid,
  plan,
  formatCurrency,
  formatPaybackTime,
  companyName,
  loanApplicationId,
  lenderName,
  isAdmin,
  initialView,
  extendedTerm,
}: IRepaymentPlanSectionProps) => {
  const { t } = useTranslation("repaymentPlan");
  const [selectedMonths, setSelectedMonths] = useState<number | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [planView, setPlanView] = useState<string>(initialView);
  // Only an admin can reach the simulation, so a customer always sees the
  // quoted plan even if a stale `isOn` were passed in.
  const isExtended = isAdmin && extendedTerm?.isOn === true;
  // Matched on the plan's own amortization method rather than a display name,
  // so it does not break when copy changes. Only BULLET/BUILDING_CREDIT needs
  // a caveat of its own: its payment is interest-only and so does not move
  // with the term at all, which `banner` would otherwise imply it does. Every
  // other method is fully described by `banner`, which already states that
  // interest is recalculated at the rate the offer implies -- so they add
  // nothing here rather than repeating that sentence.
  const methodCaveatKey: string | null =
    plan.amortizationMethod === "BULLET" ||
    plan.amortizationMethod === "BUILDING_CREDIT"
      ? "extended.bannerBullet"
      : null;
  // While extended, the fetched plan REPLACES the quoted one. Passing it with
  // months === its own full term makes the display hook's clamp a no-op and
  // leaves isEarly false, so the early-repayment savings block hides itself.
  // That is why neither useRepaymentPlanDisplay nor earlyRepayment.ts changes.
  const activePlan = (isExtended && extendedTerm?.plan) || plan;
  const fullMonths = activePlan.entries.length - 1;
  // The lender's own term. NOT fullMonths, which follows activePlan and so
  // becomes the simulated term while the switch is on.
  const quotedMonths = plan.entries.length - 1;
  // BULLET/BUILDING_CREDIT: the monthly payment is interest-only and never
  // changes with term, so the cost-increase summary omits that box entirely.
  const isBullet =
    plan.amortizationMethod === "BULLET" ||
    plan.amortizationMethod === "BUILDING_CREDIT";
  // The shorter-term slider is an admin tool, so a customer always sees the
  // full term.
  const months =
    !isAdmin || isExtended || selectedMonths === null
      ? fullMonths
      : Math.min(Math.max(selectedMonths, 1), fullMonths);

  const display = useRepaymentPlanDisplay(activePlan, months);

  // Local draft so the month value commits on blur/Enter rather than on every
  // keystroke: each commit is a network round trip whose resolver runs a
  // promotions query plus the rate solve (up to ~11 calculator passes, each
  // with its own 80-iteration bisection at prec=50 for annuity).
  //
  // Deliberately NOT debounced. A debounce still fires on every pause while
  // someone types "1", "12", "120" -- three requests for two intermediate
  // values nobody asked to see -- and adds latency to the one value they did.
  // Commit-on-blur/Enter is one request per decision. Typing, holding an arrow
  // key and clicking the number spinner all mutate the draft only.
  // null means "not locally edited" -- show the committed term. This is what
  // makes a mount with isOn already true (a remount while extended, e.g. a
  // tab switch above this component) show the real committed months instead
  // of a blank input: seeding the draft only from the Switch's own
  // onCheckedChange handler left it stuck at "" for any mount that didn't
  // fire that handler in this component instance.
  const [monthsDraft, setMonthsDraft] = useState<string | null>(null);
  const [monthsError, setMonthsError] = useState(false);
  const monthsValue = monthsDraft ?? String(extendedTerm?.months ?? "");

  // Typing commits on a debounce so the admin never has to click away to see
  // the result. Blur and Enter still commit immediately, and both cancel any
  // pending timer so a value can't land twice.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelPendingCommit = () => {
    if (debounceRef.current !== null) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  };
  // Cleanup is inlined rather than reusing cancelPendingCommit: that helper is
  // rebuilt every render, so depending on it would either re-run this effect
  // on every render or need a lint suppression. A ref read has no dependencies.
  useEffect(
    () => () => {
      if (debounceRef.current !== null) {
        clearTimeout(debounceRef.current);
      }
    },
    [],
  );

  // `value` is passed in rather than read from `monthsValue`: the debounced
  // caller is a closure from the render BEFORE setMonthsDraft lands, so
  // reading state here would commit the previous keystroke.
  //
  // `showError` separates the two paths. On an explicit commit an out-of-range
  // value has to say so; mid-typing it must not, or every keystroke toward a
  // valid term flashes the validation line ("3" on the way to "36" is below
  // the quoted minimum and would light up red).
  const commitTerm = (value: string, showError: boolean) => {
    const parsed = Number(value);
    const min = extendedTerm?.minMonths ?? 1;
    const max = extendedTerm?.maxMonths ?? 120;
    if (!Number.isInteger(parsed) || parsed < min || parsed > max) {
      if (showError) {
        setMonthsError(true);
      }
      return;
    }
    setMonthsError(false);
    // A valid value -- committed already or about to be -- follows the
    // committed term again rather than staying pinned to what was typed.
    setMonthsDraft(null);
    // Blur fires whenever focus leaves, including when nothing was typed, so
    // skip an unchanged value rather than churning container state.
    if (parsed === extendedTerm?.months) {
      return;
    }
    extendedTerm?.onMonthsCommit(parsed);
  };

  const commitMonths = () => {
    cancelPendingCommit();
    commitTerm(monthsValue, true);
  };

  const earlyRepaymentFee = (unit: EarlyRepaymentUnit, value: number) =>
    unit === EarlyRepaymentUnit.PERCENT
      ? t("bids.common.earlyRepaymentPercent", { value })
      : formatCurrency(value);

  const { summary, comment } = describeEarlyRepayment(bid.earlyRepayment);
  let earlyRepaymentFeeLabel: string;
  switch (summary.kind) {
    case "free":
      earlyRepaymentFeeLabel = t("bids.common.earlyRepaymentFree");
      break;
    case "feeApplies":
      earlyRepaymentFeeLabel = earlyRepaymentFee(summary.unit, summary.value);
      break;
    case "freeAfterPeriod":
      earlyRepaymentFeeLabel = t("bids.common.earlyRepaymentFeeUntilMonth", {
        fee: earlyRepaymentFee(summary.unit, summary.value),
        count: summary.months,
      });
      break;
    case "fullInterestAndFees":
      earlyRepaymentFeeLabel = t("bids.common.earlyRepaymentAllCostsApply");
      break;
    // A migrated bid whose fee numbers core-service could not recover. The
    // type is still real, so name it rather than falling through to "—".
    case "typeOnly":
      earlyRepaymentFeeLabel = t(TYPE_ONLY_KEY[summary.type]);
      break;
    default:
      // "—", never the comment: the comment is the lender's aside, not the
      // terms, and it gets its own labelled row/box below.
      earlyRepaymentFeeLabel = "—";
  }

  // The savings panel shows the amount CHARGED for settling at this month
  // immediately beside this tile, and in exactly ONE shape the terms repeat
  // it: a fixed-amount FEE_APPLIES, whose label IS that currency figure —
  // "Cost for early repayment 5 000,00 kr" next to "Early-repayment fee
  // 5 000,00 kr". Only that case falls back to the type name.
  //
  // Every other shape says something the amount does not, so it keeps the
  // full terms: FREE_AFTER_PERIOD carries the months ("2% for the first 6
  // months, then free"), and a percentage fee carries the rate, which is not
  // the same fact as the krona figure it works out to.
  const termsRepeatTheAmount =
    summary.kind === "feeApplies" && summary.unit === EarlyRepaymentUnit.AMOUNT;
  const earlyRepaymentTermsLabel = termsRepeatTheAmount
    ? t(TYPE_ONLY_KEY[EarlyRepaymentType.FEE_APPLIES])
    : earlyRepaymentFeeLabel;

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const { exportToXlsx } = await import("@krea/shared/utils/exportToXlsx");
      await exportToXlsx(
        buildRepaymentPlanExportSpec({
          bid,
          companyName,
          loanApplicationId,
          lenderName,
          earlyRepaymentFeeLabel,
          earlyRepaymentTermsLabel,
          earlyRepaymentComment: comment,
          hasNoCostReduction: summary.kind === "fullInterestAndFees",
          formatCurrency,
          formatPaybackTime,
          t,
          displayPlan: display.displayPlan,
          totalValue: display.totalValue,
          // Marked iff the plan actually IN VIEW is the simulated one, not
          // merely "the switch is on". On the first flip (switch on, nothing
          // committed yet) or during the loading window, `extendedTerm.plan`
          // is still null and the table is rendering the real quoted `plan`
          // -- exporting THAT unmarked-as-simulated would put the "not quoted
          // by the lender" notice over the lender's actual offer.
          extendedMonths:
            isExtended && extendedTerm?.plan ? extendedTerm.months : null,
        }),
      );
    } catch (error) {
      console.error("Failed to export amortisation plan", error);
    } finally {
      setIsExporting(false);
    }
  };

  const { displayPlan } = display;

  const chartRows: IRepaymentChartRow[] = displayPlan.entries.map((entry) => ({
    month: entry.month,
    principal: Number(entry.principal),
    interest: Number(entry.interest),
    setupFee: entry.month === 0 ? Number(entry.payment) : 0,
    payment: Number(entry.payment),
    balance: Number(entry.remainingBalance),
  }));

  // MONTHLY is the status quo on every existing offer, so its description
  // would be noise. Surface the server's explanation only when the cadence is
  // something the customer would not assume.
  const frequencyNote =
    plan.paymentFrequency === PaymentFrequency.MONTHLY
      ? null
      : bid.paymentFrequencyDescription;

  const table = (
    <RepaymentPlanTable
      display={display}
      formatCurrency={formatCurrency}
      t={t}
    />
  );

  const chart = (
    <Suspense
      fallback={<Text className="text-helper block py-2">{t("loading")}</Text>}
    >
      <RepaymentPlanChart rows={chartRows} formatCurrency={formatCurrency} />
    </Suspense>
  );

  return (
    // Rendered inside a modal whose header already titles and frames the
    // plan, so the section carries no border or title of its own.
    <section
      className="bg-white p-4 md:p-6"
      data-testid="repayment-plan-section"
    >
      <Alert
        variant="infoAlert"
        className="border-0 bg-primary-150 has-[*]:mt-0 has-[*]:mb-4"
        data-testid="plan-indicative"
        role="status"
      >
        {t("disclaimer")}
      </Alert>

      {frequencyNote && (
        <Text className="text-helper mb-3 block">{frequencyNote}</Text>
      )}

      <div className="mb-4 flex flex-wrap gap-3">
        <StatBox
          label={t("setupFee")}
          value={formatCurrency(Number(displayPlan.totals.setupFee))}
          tooltip={t("tooltips.setupFee")}
        />
        <StatBox
          label={t("totalCostOfCredit")}
          value={formatCurrency(Number(displayPlan.totals.totalCostOfCredit))}
          tooltip={t("tooltips.totalCostOfCredit")}
        />
        <StatBox
          label={t("totalRepaid")}
          value={formatCurrency(Number(displayPlan.totals.totalRepaid))}
          tooltip={t("tooltips.totalRepaid")}
        />
      </div>

      {isAdmin && (
        <div
          className="mb-4 rounded-lg border border-dashed border-gray-300 p-3"
          data-testid="repayment-plan-admin-tools"
        >
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <Badge variant="secondary">{t("adminOnly")}</Badge>
            {/* In both views: it writes the same rows whichever is showing. */}
            <Button
              variant="secondary"
              size="sm"
              onClick={handleExport}
              disabled={isExporting}
              className="shrink-0"
            >
              <FileSpreadsheet className="h-4 w-4" />
              {t(isExporting ? "exporting" : "exportToExcel")}
            </Button>
          </div>

          {fullMonths > 1 && (
            <RepaymentTermControls
              fullMonths={fullMonths}
              months={months}
              quotedMonths={quotedMonths}
              isExtended={isExtended}
              isBullet={isBullet}
              extendedTerm={extendedTerm}
              monthsValue={monthsValue}
              monthsError={monthsError}
              methodCaveatKey={methodCaveatKey}
              plan={plan}
              formatCurrency={formatCurrency}
              lenderName={lenderName}
              t={t}
              onMonthsChange={(value) => {
                setMonthsDraft(value);
                // Clear a stale validation message the moment the user
                // edits again, rather than leaving the red line up while
                // they type a now-valid value.
                setMonthsError(false);
                cancelPendingCommit();
                debounceRef.current = setTimeout(() => {
                  debounceRef.current = null;
                  commitTerm(value, false);
                }, MONTHS_COMMIT_DEBOUNCE_MS);
              }}
              commitMonths={commitMonths}
              onSelectedMonthsChange={setSelectedMonths}
              onSwitchToggle={(checked) => {
                // Follow the committed term again rather than pinning to
                // whatever was mid-edit before the flip.
                setMonthsDraft(null);
                setMonthsError(false);
                extendedTerm?.onToggle(checked);
              }}
            />
          )}
        </div>
      )}

      <Tabs value={planView} onValueChange={setPlanView}>
        <TabsList className="mb-3">
          <TabsTrigger value="graph">{t("tabs.graph")}</TabsTrigger>
          <TabsTrigger value="table">{t("tabs.table")}</TabsTrigger>
        </TabsList>
        <TabsContent value="graph">{chart}</TabsContent>
        <TabsContent value="table">{table}</TabsContent>
      </Tabs>

      <EarlyRepaymentPanels
        displayPlan={displayPlan}
        hasNoCostReduction={summary.kind === "fullInterestAndFees"}
        comment={comment}
        earlyRepaymentTermsLabel={earlyRepaymentTermsLabel}
        formatCurrency={formatCurrency}
        formatPaybackTime={formatPaybackTime}
        t={t}
      />
    </section>
  );
};
